/* eslint-disable sort-imports */
/**
 * One labor standard in the catalog: title, work centers and batch, the two
 * stats, the per-role bar, and (expanded) every operation with its phases.
 */
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PreviewIcon from "@mui/icons-material/Preview";
import { Box, Collapse, Grid2, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useCallback } from "react";

import { LaborStandard, LaborStandardOperation } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import { MonoText, planner,PlannerCard,roleColor,SectionLabel } from "../../planner-ui";
import { formatCrew, isMachinePhase, standardWorkstations } from "../../utils/standard-matching";
import {
	formatDuration,
	getRoleBreakdown,
	machineLineLabel,
	operationCategory,
	operationLabel,
	workPhaseLabel,
} from "../utils/standard-helpers";
import { categoryTagSx, deleteIconButtonSx, iconButtonSx, machineChipSx, sequenceBadgeSx, workcenterChipSx } from "./styles";

// ---------------------------------------------------------------------------

/** Per-role minutes, as a proportional bar with a three-role legend. */
const RoleBar: React.FC<{ breakdown: [string, number][] }> = ({ breakdown }) => {
	if (breakdown.length === 0) return null;
	const totalMinutes = breakdown.reduce((sum, [, minutes]) => sum + minutes, 0);
	return (
		<Box sx={{ mt: 1.5 }}>
			<Box
				sx={{
					display: "flex",
					height: 6,
					borderRadius: `${planner.radiusXs}px`,
					overflow: "hidden",
					bgcolor: planner.unplannedSoft,
				}}
			>
				{breakdown.map(([role, minutes]) => (
					<Tooltip key={role} title={`${role} — ${formatDuration(minutes)}`} arrow>
						<Box
							sx={{
								flexGrow: Math.max(minutes / totalMinutes, 0.02),
								flexBasis: 0,
								bgcolor: roleColor(role),
								"&:not(:first-of-type)": { ml: "1px" },
							}}
						/>
					</Tooltip>
				))}
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.75, flexWrap: "wrap", minHeight: 18 }}>
				{breakdown.slice(0, 3).map(([role]) => (
					<Box key={role} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
						<Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: roleColor(role), flexShrink: 0 }} />
						<Typography noWrap sx={{ ...planner.type.meta, color: planner.inkMuted, maxWidth: 120 }}>
							{role}
						</Typography>
					</Box>
				))}
				{breakdown.length > 3 && (
					<Typography sx={{ ...planner.type.meta, color: planner.inkMuted }}>+{breakdown.length - 3} more</Typography>
				)}
			</Box>
		</Box>
	);
};

// ---------------------------------------------------------------------------

const OperationDetailRow: React.FC<{ standard: LaborStandard; operation: LaborStandardOperation; index: number; last: boolean }> = ({
	standard,
	operation,
	index,
	last,
}) => (
	<Box sx={{ display: "flex", gap: 1.25, py: 1.25, borderBottom: last ? "none" : `1px solid ${planner.hairline}` }}>
		<Box sx={{ ...sequenceBadgeSx, mt: 0.125 }}>{operation.sequence || index + 1}</Box>
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
				{/* The operation's own name leads; its type is the category behind it. */}
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
					<Typography noWrap sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
						{operationLabel(operation)}
					</Typography>
					{operationCategory(operation) ? (
						<Box component="span" sx={categoryTagSx}>
							{operationCategory(operation)}
						</Box>
					) : null}
				</Box>
				<Typography
					sx={{
						...planner.type.metaStrong,
						fontFamily: planner.fontMono,
						color: planner.inkSecondary,
						fontVariantNumeric: "tabular-nums",
						flexShrink: 0,
					}}
				>
					{formatDuration(operation.durationMinutes)}
				</Typography>
			</Box>
			{/* Where the step runs: its work center, and the alternatives it may run on */}
			{(operation.workstation || standard.workstation) && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
					<Box component="span" sx={workcenterChipSx}>
						{operation.workstation || standard.workstation}
					</Box>
					{(operation.workstationOptions || []).map((option) => (
						<Tooltip
							key={option.id ?? option.workstation}
							title={
								option.durationMinutes
									? `Alternative — ${formatDuration(option.durationMinutes)} on this work center`
									: "Alternative work center"
							}
							arrow
						>
							<Box component="span" sx={{ ...planner.type.meta, color: planner.inkMuted }}>
								or{" "}
								<Box component="span" sx={{ color: planner.inkSecondary, fontWeight: 600 }}>
									{option.workstation}
								</Box>
							</Box>
						</Tooltip>
					))}
				</Box>
			)}
			{operation.notes && (
				<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 0.25 }}>{operation.notes}</Typography>
			)}
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
				{operation.phases?.map((phase, phaseIdx) =>
					isMachinePhase(phase) ? (
						// Machine time: the unit is busy, nobody is needed. Neutral,
						// no role dot, never part of the role bar.
						<Tooltip
							key={phase.id || phaseIdx}
							title={`${workPhaseLabel(phase) ? `${workPhaseLabel(phase)} · ` : ""}occupies the unit, no workers · starts +${phase.startOffsetMinutes} min${phase.notes ? ` · ${phase.notes}` : ""}`}
							arrow
						>
							<Box component="span" sx={machineChipSx}>
								{machineLineLabel(phase, phase.durationMinutes)}
							</Box>
						</Tooltip>
					) : (
						<Tooltip
							key={phase.id || phaseIdx}
							title={`${workPhaseLabel(phase) ? `${workPhaseLabel(phase)} · ` : ""}${formatCrew(phase.workersRequired)} worker(s) · starts +${phase.startOffsetMinutes} min${phase.notes ? ` · ${phase.notes}` : ""}`}
							arrow
						>
							<Box
								component="span"
								sx={{
									...planner.type.meta,
									fontFamily: planner.fontUi,
									display: "inline-flex",
									alignItems: "center",
									gap: 0.6,
									px: 1,
									py: 0.35,
									borderRadius: `${planner.radiusSm}px`,
									border: `1px solid ${planner.hairline}`,
									bgcolor: planner.surfaceSubtle,
									color: planner.inkSecondary,
									whiteSpace: "nowrap",
								}}
							>
								<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: roleColor(phase.jobRole), flexShrink: 0 }} />
								{/* The customer's own work-type code leads when the phase is
								    linked to a work phase; the job role still follows it. */}
								{phase.workPhaseCode ? (
									<Box component="span" sx={{ ...planner.type.metaStrong, fontFamily: planner.fontMono, color: planner.accentDeep }}>
										{phase.workPhaseCode}
									</Box>
								) : null}
								{phase.workPhaseName ? (
									<Box component="span" sx={{ color: planner.inkMuted }}>
										{phase.workPhaseName}
									</Box>
								) : null}
								{phase.workPhaseCode || phase.workPhaseName ? (
									<Box component="span" sx={{ color: planner.hairlineStrong }}>
										·
									</Box>
								) : null}
								{phase.jobRole}
								<Box component="span" sx={{ ...planner.type.metaStrong, fontFamily: planner.fontMono, fontVariantNumeric: "tabular-nums" }}>
									{formatDuration(phase.durationMinutes)}
								</Box>
								{phase.workersRequired !== 1 && (
									<Box component="span" sx={{ fontFamily: planner.fontMono, color: planner.inkMuted }}>
										×{formatCrew(phase.workersRequired)}
									</Box>
								)}
							</Box>
						</Tooltip>
					)
				)}
			</Box>
		</Box>
	</Box>
);

// ---------------------------------------------------------------------------

const statSx = {
	...planner.type.stat,
	fontFamily: planner.fontMono,
	color: planner.navy,
	fontVariantNumeric: "tabular-nums",
	lineHeight: 1.3,
} as const;

export interface StandardCardProps {
	standard: LaborStandard;
	product?: Product;
	isExpanded: boolean;
	/** Precomputed role -> minutes; falls back to computing it for an unsaved standard. */
	breakdown?: [string, number][];
	onToggle: (standardId: number) => void;
	onPreview: (standard: LaborStandard) => void;
	onEdit: (standard: LaborStandard) => void;
	onDelete: (standardId: number) => void;
}

const StandardCard: React.FC<StandardCardProps> = ({ standard, product, isExpanded, breakdown, onToggle, onPreview, onEdit, onDelete }) => {
	const displayName = standard.productName || product?.name || standard.sku || "Untitled standard";
	const displaySku = standard.sku || standard.productItemNumber || product?.itemNumber || "";
	const operationsCount = standard.operations?.length || 0;
	const workCenters = standardWorkstations(standard);
	const roleBreakdown = breakdown ?? getRoleBreakdown(standard);
	const operations = standard.operations ?? [];

	const handleToggle = useCallback(() => onToggle(standard.id), [onToggle, standard.id]);
	const handlePreview = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onPreview(standard);
		},
		[onPreview, standard]
	);
	const handleEdit = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onEdit(standard);
		},
		[onEdit, standard]
	);
	const handleDelete = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onDelete(standard.id);
		},
		[onDelete, standard.id]
	);

	return (
		<Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
			<PlannerCard onClick={handleToggle} sx={{ p: 2, height: "100%" }}>
				{/* Title row */}
				<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
					<Box sx={{ minWidth: 0 }}>
						<Typography noWrap sx={{ ...planner.type.bodyStrong, color: planner.ink, lineHeight: 1.35 }}>
							{displayName}
						</Typography>
						{displaySku ? <MonoText>{displaySku}</MonoText> : null}
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
						<Tooltip title="Preview Timeline">
							<IconButton size="small" onClick={handlePreview} sx={iconButtonSx}>
								<PreviewIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Edit">
							<IconButton size="small" onClick={handleEdit} sx={iconButtonSx}>
								<EditIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Delete">
							<IconButton size="small" onClick={handleDelete} sx={deleteIconButtonSx}>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>

				{/* Recipe group + work centers + batch */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.25, flexWrap: "wrap" }}>
					{standard.recipeGroup ? (
						<Tooltip title="Recipe group" arrow>
							<Box
								component="span"
								sx={{ ...workcenterChipSx, fontFamily: planner.fontMono, bgcolor: planner.surface, color: planner.navy, borderColor: planner.hairlineStrong }}
							>
								{standard.recipeGroup}
							</Box>
						</Tooltip>
					) : null}
					{workCenters.slice(0, 3).map((name) => (
						<Box key={name} component="span" sx={workcenterChipSx}>
							{name}
						</Box>
					))}
					{workCenters.length > 3 ? (
						<Tooltip title={workCenters.slice(3).join(", ")} arrow>
							<Box component="span" sx={{ ...planner.type.meta, color: planner.inkMuted }}>
								+{workCenters.length - 3} more
							</Box>
						</Tooltip>
					) : null}
					<Box
						component="span"
						sx={{
							display: "inline-flex",
							alignItems: "center",
							px: 1.25,
							py: 0.4,
							...planner.type.metaStrong,
							fontFamily: planner.fontMono,
							borderRadius: `${planner.radiusSm}px`,
							bgcolor: planner.unplannedSoft,
							border: `1px solid ${planner.hairline}`,
							color: planner.inkSecondary,
							lineHeight: 1.4,
							fontVariantNumeric: "tabular-nums",
							whiteSpace: "nowrap",
						}}
					>
						Batch {standard.batchSize.toLocaleString()}
						{standard.batchUnit ? ` ${standard.batchUnit}` : ""}
					</Box>
				</Box>

				{/* Stats */}
				<Box sx={{ display: "flex", alignItems: "flex-end", gap: 3, mt: 1.75 }}>
					<Box>
						<SectionLabel>Total time</SectionLabel>
						<Typography sx={statSx}>{formatDuration(standard.totalDurationMinutes)}</Typography>
					</Box>
					<Box>
						<SectionLabel>Operations</SectionLabel>
						<Typography sx={statSx}>{operationsCount}</Typography>
					</Box>
					<Box sx={{ flexGrow: 1 }} />
					<ExpandMoreIcon
						sx={{
							color: planner.inkMuted,
							fontSize: 20,
							transition: "transform 150ms ease",
							transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
						}}
					/>
				</Box>

				{/* Per-role minutes breakdown */}
				<RoleBar breakdown={roleBreakdown} />

				{/* Expanded operation detail */}
				<Collapse in={isExpanded} timeout={180} unmountOnExit>
					<Box sx={{ mt: 1.5, pt: 0.5, borderTop: `1px solid ${planner.hairline}` }}>
						{operations.map((operation, opIdx) => (
							<OperationDetailRow
								key={operation.id || opIdx}
								standard={standard}
								operation={operation}
								index={opIdx}
								last={opIdx >= operations.length - 1}
							/>
						))}
					</Box>
				</Collapse>
			</PlannerCard>
		</Grid2>
	);
};

export default StandardCard;
